import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@hp/ui";
import { listClaimChatMessages, sendClaimChatMessage } from "../data/claimChat";
import { ErrorState, LoadingState } from "./Feedback";
import { formatDateTime } from "../lib/workbenchFormat";

/**
 * The original POC brief's chat feature ("ask why it was flagged, pull
 * the source order, explore the numbers"), scoped to one claim
 * candidate's own finding/precedent chain/matched clause - bounded RAG,
 * not open-corpus search, the same way the extraction pipeline's citation
 * gate keeps hallucination risk low.
 */
export function ClaimChatPanel({ claimCandidateId }: { claimCandidateId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState("");
  const listRef = React.useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["claim-chat", claimCandidateId],
    queryFn: () => listClaimChatMessages(claimCandidateId),
  });

  const sendMutation = useMutation({
    mutationFn: (message: string) => sendClaimChatMessage(claimCandidateId, message),
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["claim-chat", claimCandidateId] });
    },
  });

  // Keep the newest message in view by scrolling the message list itself.
  // `scrollIntoView` would also scroll the whole PAGE down to this panel on
  // first load (the list "changes" from undefined to empty), pushing the
  // claim header - and its "View claim file" button - out of sight.
  React.useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
  }, [data?.length, sendMutation.isPending]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    sendMutation.mutate(trimmed);
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-slate-500">
        Ask why this was flagged, what each financial year contributes, how principal and LPS add up, or what the
        source orders said — answers are grounded in this claim's findings, FY tranches, precedent chain, and matched
        clause.
      </p>

      {isLoading ? <LoadingState label="Loading conversation..." /> : null}
      {isError ? <ErrorState error={error} label="Could not load the conversation" /> : null}

      <div ref={listRef} className="flex max-h-96 flex-col gap-3 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-3">
        {data && data.length === 0 ? (
          <p className="text-center text-sm text-slate-400">No questions asked yet - try one below.</p>
        ) : null}
        {data?.map((message) => (
          <div
            key={message.id}
            className={[
              "max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-line",
              message.role === "user"
                ? "self-end bg-brand-600 text-white"
                : "self-start border border-slate-200 bg-white text-slate-700",
            ].join(" ")}
          >
            {message.content}
            <div className={["mt-1 text-[0.625rem]", message.role === "user" ? "text-brand-100" : "text-slate-400"].join(" ")}>
              {formatDateTime(message.created_at)}
            </div>
          </div>
        ))}
        {sendMutation.isPending ? (
          <div className="self-start rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-400">
            Thinking...
          </div>
        ) : null}
      </div>

      {sendMutation.isError ? <ErrorState error={sendMutation.error} label="Could not send that message" /> : null}

      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleSubmit(event);
            }
          }}
          rows={2}
          placeholder="e.g. What is the total across all FYs? Which order backs FY2019-20?"
          className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <Button type="submit" disabled={sendMutation.isPending || draft.trim() === ""}>
          Ask
        </Button>
      </form>
    </div>
  );
}
